import { useCompanyLabel, useCompanyLogo } from "@/components/CompanyInfoBanner";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PasswordConfirmDialog } from "@/components/PasswordConfirmDialog";
import { Plus, PencilSimple, Trash, Wrench, Sparkle, Recycle, ClockCounterClockwise, Truck, ListBullets, FilePdf, Brain, Warning } from "@phosphor-icons/react";
import { parseNum, fmtDate } from "@/lib/utils";
import { BR_STATES } from "@/lib/br";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import jsPDF from "jspdf";

const EMPTY = {
  vehicle_id: "", trip_id: "", type: "preventiva", date: "", description: "",
  km_at_service: "", next_service_km: "",
  service_place: "", city: "", uf: "",
  part_name: "", part_condition: "nova", part_quantity: "", part_unit_value: "", part_total_value: 0,
  labor_cost: "",
  cost: 0,
  paid_by: "empresa",
};
const fmt = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Maintenance() {
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [pwAction, setPwAction] = useState(null);

  const load = async () => { const [m, v] = await Promise.all([api.get("/maintenance"), api.get("/vehicles")]); setItems(m.data); setVehicles(v.data); };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const h = (e) => { if (e.detail?.entity === "maintenance") load(); };
    window.addEventListener("live-event", h);
    return () => window.removeEventListener("live-event", h);
  }, []);

  // Auto-calc part total & overall cost
  useEffect(() => {
    const partTotal = parseNum(form.part_quantity) * parseNum(form.part_unit_value);
    const overall = partTotal + parseNum(form.labor_cost);
    setForm((f) => ({ ...f, part_total_value: partTotal, cost: overall }));
    // eslint-disable-next-line
  }, [form.part_quantity, form.part_unit_value, form.labor_cost]);

  const set = (k, isNum = false) => (e) => {
    const raw = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: raw }));
  };
  const MAINT_NUM_FIELDS = ["km_at_service", "next_service_km", "part_quantity", "part_unit_value", "labor_cost"];
  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    MAINT_NUM_FIELDS.forEach((k) => { payload[k] = parseNum(payload[k]); });
    payload.part_total_value = parseNum(payload.part_total_value);
    payload.cost = parseNum(payload.cost);
    if (editingId) return setPwAction({ kind: "edit", id: editingId, payload });
    try { await api.post("/maintenance", payload); toast.success("Manutenção registrada"); setOpen(false); setForm(EMPTY); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };
  const startEdit = (item) => { setForm({ ...EMPTY, ...item }); setEditingId(item.id); setOpen(true); };
  const remove = (id) => setPwAction({ kind: "delete", id });
  const runPwAction = async () => {
    try {
      if (pwAction.kind === "delete") {
        await api.delete(`/maintenance/${pwAction.id}`);
        toast.success("Manutenção removida");
      } else {
        await api.put(`/maintenance/${pwAction.id}`, pwAction.payload);
        toast.success("Manutenção atualizada");
        setOpen(false); setEditingId(null); setForm(EMPTY);
      }
      setPwAction(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
            <Wrench size={32} weight="duotone" className="text-blue-600" /> Manutenção
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Preventiva/Corretiva com controle de peças e mão-de-obra</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button onClick={() => { setForm(EMPTY); setEditingId(null); setOpen(true); }} data-testid="btn-new-maint" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white gap-1.5"><Plus size={16} /> Nova</Button></DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingId ? "Editar Manutenção (requer senha)" : "Nova Manutenção"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 xl:grid-cols-4 gap-3">
              <div className="col-span-2 md:col-span-4"><Label>Veículo</Label>
                <Select value={form.vehicle_id} onValueChange={set("vehicle_id")}>
                  <SelectTrigger data-testid="maint-vehicle"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>{vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.plate_truck} — {v.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Tipo</Label>
                <Select value={form.type} onValueChange={set("type")}>
                  <SelectTrigger data-testid="maint-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="preventiva">Preventiva</SelectItem><SelectItem value="corretiva">Corretiva</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Data</Label><Input type="date" value={form.date} onChange={set("date")} data-testid="maint-date" /></div>

              {/* Local do serviço */}
              <div className="col-span-2 md:col-span-4 mt-1 text-xs font-bold uppercase tracking-wider text-blue-700">Local do serviço</div>
              <div className="col-span-2"><Label>Oficina / Prestador</Label>
                <Input value={form.service_place} onChange={set("service_place")} placeholder="Ex: Mecânica São Jorge" data-testid="maint-service-place" />
              </div>
              <div><Label>UF</Label>
                <Select value={form.uf} onValueChange={set("uf")}>
                  <SelectTrigger data-testid="maint-uf"><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>{BR_STATES.map(([uf]) => <SelectItem key={uf} value={uf}>{uf}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Cidade</Label>
                <CityAutocomplete value={form.city} uf={form.uf} onChange={set("city")} placeholder="Digite a cidade" data-testid="maint-city" />
              </div>

              <div className="col-span-2 md:col-span-4 mt-1 text-xs font-bold uppercase tracking-wider text-blue-700">Descrição & KM</div>
              <div className="col-span-2 md:col-span-4"><Label>Descrição do serviço</Label>
                <Input value={form.description} onChange={set("description")} placeholder="Ex: Troca de pastilhas dianteiras + alinhamento" />
              </div>
              <div className="col-span-2"><Label>KM na manutenção</Label>
                <Input type="text" inputMode="decimal" value={form.km_at_service} onChange={set("km_at_service", true)} data-testid="maint-km" />
              </div>
              <div className="col-span-2"><Label>KM próxima revisão</Label>
                <Input type="text" inputMode="decimal" value={form.next_service_km} onChange={set("next_service_km", true)} data-testid="maint-next-km" />
              </div>

              {/* Peças */}
              <div className="col-span-2 md:col-span-4 mt-1 text-xs font-bold uppercase tracking-wider text-blue-700">Peças</div>
              <div className="col-span-2 md:col-span-3"><Label>Peça / Descrição</Label>
                <Input value={form.part_name} onChange={set("part_name")} placeholder="Ex: Pastilha de freio dianteira" data-testid="maint-part-name" />
              </div>
              <div><Label>Condição</Label>
                <Select value={form.part_condition} onValueChange={set("part_condition")}>
                  <SelectTrigger data-testid="maint-part-condition"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nova"><span className="inline-flex items-center gap-2"><Sparkle size={14} weight="fill" className="text-emerald-600" /> Peça Nova</span></SelectItem>
                    <SelectItem value="reparada"><span className="inline-flex items-center gap-2"><Recycle size={14} weight="fill" className="text-amber-600" /> Peça Reparada / Usada</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Quantidade</Label>
                <Input type="text" inputMode="decimal" placeholder="ex: 2" value={form.part_quantity} onChange={set("part_quantity", true)} data-testid="maint-part-qty" />
              </div>
              <div><Label>Valor unitário</Label>
                <Input type="text" inputMode="decimal" placeholder="ex: 180,00" value={form.part_unit_value} onChange={set("part_unit_value", true)} data-testid="maint-part-unit" />
              </div>
              <div className="col-span-2"><Label>Total peças (auto)</Label>
                <Input readOnly value={fmt(form.part_total_value)} className="bg-emerald-50 font-bold" data-testid="maint-part-total" />
              </div>

              <div className="col-span-2"><Label>Mão-de-obra (R$)</Label>
                <Input type="text" inputMode="decimal" placeholder="ex: 350,00" value={form.labor_cost} onChange={set("labor_cost", true)} data-testid="maint-labor" />
              </div>
              <div className="col-span-2"><Label>💰 Custo total (auto)</Label>
                <Input readOnly value={fmt(form.cost)} className="bg-blue-50 font-bold text-lg" data-testid="maint-total" />
              </div>

              <div className="col-span-2 md:col-span-4"><Label>Pago por</Label>
                <Select value={form.paid_by} onValueChange={set("paid_by")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="empresa">Empresa</SelectItem><SelectItem value="motorista">Motorista</SelectItem></SelectContent>
                </Select>
              </div>
              <DialogFooter className="col-span-2 md:col-span-4"><Button type="submit" data-testid="btn-save-maint" className="bg-blue-600 hover:bg-blue-700 hover:text-white text-white">Salvar</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="list">
        <TabsList className="h-auto p-1">
          <TabsTrigger value="list" data-testid="maint-tab-list" className="py-2 px-4 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white"><ListBullets size={16} /> Lista</TabsTrigger>
          <TabsTrigger value="history" data-testid="maint-tab-history" className="py-2 px-4 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white"><ClockCounterClockwise size={16} /> Histórico por Veículo</TabsTrigger>
        </TabsList>

        <TabsContent value="list">
          <Card>
            <CardHeader><CardTitle className="text-lg">Manutenções ({items.length})</CardTitle></CardHeader>
            <CardContent>
              {items.length === 0 ? <p className="text-muted-foreground text-sm">Nenhuma manutenção registrada.</p> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Serviço / Peças</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead className="text-right">KM</TableHead>
                    <TableHead className="text-right">Custo</TableHead>
                    <TableHead></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {items.map((i) => (
                      <TableRow key={i.id} data-testid={`maint-row-${i.id}`}>
                        <TableCell className="text-xs">{fmtDate(i.date)}</TableCell>
                        <TableCell><span className={`px-2 py-0.5 rounded text-xs capitalize ${i.type === "preventiva" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{i.type}</span></TableCell>
                        <TableCell className="text-xs">
                          <div className="font-semibold">{i.description}</div>
                          {i.part_name && (
                            <div className="text-slate-600 mt-0.5 inline-flex items-center gap-1">
                              {i.part_condition === "reparada"
                                ? <><Recycle size={12} weight="fill" className="text-amber-600" /> {i.part_name} · Reparada · {i.part_quantity}× {fmt(i.part_unit_value)}</>
                                : <><Sparkle size={12} weight="fill" className="text-emerald-600" /> {i.part_name} · Nova · {i.part_quantity}× {fmt(i.part_unit_value)}</>
                              }
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {i.service_place && <div>{i.service_place}</div>}
                          {(i.city || i.uf) && <div className="text-slate-500">{i.city}{i.uf ? `/${i.uf}` : ""}</div>}
                        </TableCell>
                        <TableCell className="text-right text-xs">
                          <div>{i.km_at_service}</div>
                          {i.next_service_km ? <div className="text-blue-700">próx: {i.next_service_km}</div> : null}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{fmt(i.cost)}</TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button size="icon" variant="ghost" onClick={() => startEdit(i)} data-testid={`btn-edit-maint-${i.id}`} title="Editar (requer senha)"><PencilSimple size={16} className="text-blue-600" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => remove(i.id)} data-testid={`btn-delete-maint-${i.id}`} title="Excluir (requer senha)"><Trash size={16} className="text-red-600" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <VehicleTimeline vehicles={vehicles} items={items} onRemove={remove} />
        </TabsContent>
      </Tabs>

      <PasswordConfirmDialog
        open={!!pwAction}
        onOpenChange={(v) => !v && setPwAction(null)}
        action={pwAction?.kind === "delete" ? "exclusão" : "edição"}
        description={pwAction?.kind === "delete"
          ? "Esta manutenção será removida permanentemente. Confirme sua senha para continuar."
          : "Você está prestes a editar uma manutenção salva. Confirme sua senha para aplicar as mudanças."}
        variant={pwAction?.kind === "delete" ? "destructive" : "default"}
        onConfirm={runPwAction}
      />
    </div>
  );
}

function VehicleTimeline({ vehicles, items, onRemove }) {
  const companyLabel = useCompanyLabel();
  const logo = useCompanyLogo();
  const [vehicleId, setVehicleId] = useState("");
  useEffect(() => { if (!vehicleId && vehicles.length > 0) setVehicleId(vehicles[0].id); }, [vehicles, vehicleId]);
  const vehicle = vehicles.find((v) => v.id === vehicleId);
  const vehicleItems = items.filter((i) => i.vehicle_id === vehicleId).sort((a, b) => (b.km_at_service || 0) - (a.km_at_service || 0));
  const totalCost = vehicleItems.reduce((s, i) => s + (i.cost || 0), 0);
  const totalParts = vehicleItems.reduce((s, i) => s + (i.part_total_value || 0), 0);
  const totalLabor = vehicleItems.reduce((s, i) => s + (i.labor_cost || 0), 0);
  const maxKm = vehicleItems[0]?.km_at_service || 0;
  const minKm = vehicleItems[vehicleItems.length - 1]?.km_at_service || 0;
  const kmDiff = maxKm - minKm;

  // Smart part predictions
  const partPredictions = computePartPredictions(vehicleItems, maxKm);

  const downloadPDF = () => {
    try {
      const pdf = new jsPDF({ unit: "mm", format: "a4" });
      const page = { w: 210, h: 297 };
      const margin = 12;
      let y = margin;

      // Header
      pdf.setFillColor(37, 99, 235);
      pdf.rect(0, 0, page.w, 22, "F");
      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(16); pdf.setFont("helvetica", "bold");
      pdf.text("ACERTO RÁPIDO JVO", margin, 10);
      pdf.setFontSize(10); pdf.setFont("helvetica", "normal");
      pdf.text("Histórico de Manutenção do Veículo", margin, 16);
      if (companyLabel) pdf.text(companyLabel.slice(0, 70), page.w - margin, logo ? 26 : 16, { align: "right" });
      if (logo) pdf.addImage(logo.url, logo.format, page.w - margin - Math.min(40, 12 * logo.ratio), 6, Math.min(40, 12 * logo.ratio), 12);
      pdf.setTextColor(0, 0, 0);
      y = 30;

      pdf.setFontSize(14); pdf.setFont("helvetica", "bold");
      pdf.text(`${vehicle?.plate_truck || "?"} — ${vehicle?.model || ""}`, margin, y); y += 6;
      pdf.setFontSize(9); pdf.setFont("helvetica", "normal");
      pdf.setTextColor(90);
      const meta = [];
      if (vehicle?.year) meta.push(`Ano: ${vehicle.year}`);
      if (vehicle?.color) meta.push(`Cor: ${vehicle.color}`);
      if (vehicle?.axles) meta.push(`Eixos: ${vehicle.axles}`);
      if (meta.length) { pdf.text(meta.join(" · "), margin, y); y += 5; }
      pdf.setTextColor(0);

      // Summary box
      y += 3;
      pdf.setFillColor(239, 246, 255);
      pdf.rect(margin, y, page.w - 2 * margin, 22, "F");
      pdf.setFontSize(9); pdf.setFont("helvetica", "bold");
      const cellW = (page.w - 2 * margin) / 4;
      const labels = [["Manutenções", vehicleItems.length], ["Peças", fmt(totalParts)], ["Mão-de-obra", fmt(totalLabor)], ["Total gasto", fmt(totalCost)]];
      labels.forEach(([l, v], i) => {
        pdf.setFontSize(7); pdf.setFont("helvetica", "normal"); pdf.setTextColor(37, 99, 235);
        pdf.text(String(l).toUpperCase(), margin + i * cellW + 3, y + 6);
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(0);
        pdf.text(String(v), margin + i * cellW + 3, y + 14);
      });
      y += 27;

      if (kmDiff > 0) {
        pdf.setFontSize(9); pdf.setFont("helvetica", "italic"); pdf.setTextColor(90);
        pdf.text(`KM cobertos: ${kmDiff.toLocaleString("pt-BR")} (${minKm.toLocaleString("pt-BR")} → ${maxKm.toLocaleString("pt-BR")})`, margin, y);
        y += 6;
      }

      // Predictions section
      if (partPredictions.length > 0) {
        pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(37, 99, 235);
        pdf.text("Análise inteligente de peças", margin, y); y += 5;
        pdf.setFontSize(8); pdf.setFont("helvetica", "normal"); pdf.setTextColor(0);
        for (const p of partPredictions) {
          if (y > page.h - 20) { pdf.addPage(); y = margin; }
          const status = p.status === "overdue" ? "[ATRASADO]" : p.status === "critical" ? "[URGENTE]" : p.status === "soon" ? "[LOGO]" : "[OK]";
          pdf.text(`${status} ${p.part_name} — troca a cada ${p.avg_interval.toLocaleString("pt-BR")} km · última em ${p.last_km.toLocaleString("pt-BR")} · próxima estimada ${p.next_estimated_km.toLocaleString("pt-BR")}`, margin, y); y += 4;
        }
        y += 4;
      }

      // Timeline entries
      pdf.setFontSize(11); pdf.setFont("helvetica", "bold"); pdf.setTextColor(37, 99, 235);
      pdf.text("Histórico completo", margin, y); y += 5;
      pdf.setTextColor(0);

      for (const it of vehicleItems) {
        if (y > page.h - 40) { pdf.addPage(); y = margin; }
        pdf.setDrawColor(200); pdf.setLineWidth(0.2);
        pdf.line(margin, y, page.w - margin, y);
        y += 4;
        pdf.setFontSize(9); pdf.setFont("helvetica", "bold");
        pdf.text(`${(it.type || "").toUpperCase()} · ${fmtDate(it.date)} · KM ${(it.km_at_service || 0).toLocaleString("pt-BR")}`, margin, y);
        pdf.setFont("helvetica", "normal");
        pdf.text(fmt(it.cost || 0), page.w - margin, y, { align: "right" });
        y += 5;
        if (it.description) { pdf.setFontSize(9); pdf.text(String(it.description).slice(0, 90), margin, y); y += 4; }
        if (it.part_name) {
          pdf.setFontSize(8); pdf.setTextColor(80);
          pdf.text(`Peça: ${it.part_name} · ${it.part_condition === "reparada" ? "Reparada" : "Nova"} · ${it.part_quantity || 0}× ${fmt(it.part_unit_value || 0)} = ${fmt(it.part_total_value || 0)}`, margin, y);
          pdf.setTextColor(0); y += 4;
        }
        if (it.labor_cost) { pdf.setFontSize(8); pdf.setTextColor(80); pdf.text(`Mão-de-obra: ${fmt(it.labor_cost)}`, margin, y); pdf.setTextColor(0); y += 4; }
        if (it.service_place || it.city) {
          pdf.setFontSize(8); pdf.setTextColor(120);
          pdf.text(`Local: ${it.service_place || "?"}${it.city ? ` · ${it.city}${it.uf ? "/" + it.uf : ""}` : ""}`, margin, y);
          pdf.setTextColor(0); y += 4;
        }
        if (it.next_service_km) { pdf.setFontSize(8); pdf.setTextColor(37, 99, 235); pdf.text(`Próxima revisão prevista em KM ${it.next_service_km.toLocaleString("pt-BR")}`, margin, y); pdf.setTextColor(0); y += 4; }
        y += 2;
      }

      // Footer
      const total = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7); pdf.setTextColor(150);
        pdf.text(`Página ${i}/${total} · Emitido em ${new Date().toLocaleString("pt-BR")} · Acerto Rápido JVO`, page.w / 2, page.h - 6, { align: "center" });
      }

      const fname = `historico-manutencao-${(vehicle?.plate_truck || "veiculo").replace(/[^\w]/g, "_")}-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fname);
      toast.success("PDF gerado!");
    } catch (e) { toast.error("Erro ao gerar PDF: " + e.message); }
  };

  if (vehicles.length === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground">Cadastre um veículo primeiro para ver o histórico.</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Truck size={24} weight="duotone" className="text-blue-600" />
              <Select value={vehicleId} onValueChange={setVehicleId}>
                <SelectTrigger className="w-64" data-testid="history-vehicle-select"><SelectValue placeholder="Escolha um veículo" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.plate_truck} — {v.model}</SelectItem>)}</SelectContent>
              </Select>
              {vehicleItems.length > 0 && (
                <Button onClick={downloadPDF} data-testid="btn-download-history-pdf" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 gap-1.5">
                  <FilePdf size={16} weight="fill" /> Baixar PDF
                </Button>
              )}
            </div>
            {vehicleItems.length > 0 && (
              <div className="text-sm text-right">
                <div className="text-muted-foreground text-xs">Total gasto neste veículo</div>
                <div className="text-2xl font-extrabold text-red-700" style={{ fontFamily: "Manrope" }}>{fmt(totalCost)}</div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {vehicleItems.length === 0 ? (
            <p className="text-muted-foreground text-sm py-8 text-center">Este veículo ainda não tem manutenções registradas.</p>
          ) : (
            <>
              {/* Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <div className="border rounded-lg p-3 bg-blue-50/50">
                  <div className="text-xs uppercase tracking-wider text-blue-700">Manutenções</div>
                  <div className="text-xl font-extrabold">{vehicleItems.length}</div>
                </div>
                <div className="border rounded-lg p-3 bg-emerald-50/50">
                  <div className="text-xs uppercase tracking-wider text-emerald-700">Peças</div>
                  <div className="text-xl font-extrabold">{fmt(totalParts)}</div>
                </div>
                <div className="border rounded-lg p-3 bg-amber-50/50">
                  <div className="text-xs uppercase tracking-wider text-amber-700">Mão-de-obra</div>
                  <div className="text-xl font-extrabold">{fmt(totalLabor)}</div>
                </div>
                <div className="border rounded-lg p-3 bg-slate-50">
                  <div className="text-xs uppercase tracking-wider text-slate-700">KM cobertos</div>
                  <div className="text-xl font-extrabold">{kmDiff.toLocaleString("pt-BR")}</div>
                  <div className="text-xs text-slate-500">{minKm.toLocaleString("pt-BR")} → {maxKm.toLocaleString("pt-BR")}</div>
                </div>
              </div>

              {/* Smart Part Predictions */}
              {partPredictions.length > 0 && (
                <div className="mb-6 border-2 border-purple-200 rounded-lg p-4 bg-gradient-to-br from-purple-50/60 to-blue-50/60" data-testid="part-predictions">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain size={22} weight="duotone" className="text-purple-700" />
                    <div className="font-bold text-purple-900">Alerta Inteligente por Peça</div>
                    <span className="text-xs text-purple-700">Baseado no seu histórico</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    {partPredictions.map((p) => {
                      const status = p.status;
                      const bg = status === "overdue" ? "bg-red-50 border-red-300" : status === "critical" ? "bg-amber-50 border-amber-300" : status === "soon" ? "bg-yellow-50 border-yellow-300" : "bg-white border-slate-200";
                      const label = status === "overdue" ? "🚨 ATRASADO" : status === "critical" ? "⚠️ URGENTE" : status === "soon" ? "🔔 Logo" : "✅ Em dia";
                      return (
                        <div key={p.part_name} data-testid={`prediction-${p.part_name.replace(/\s+/g, '-')}`} className={`border rounded-md p-3 text-sm ${bg}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="font-bold">{p.part_name}</div>
                            <span className="text-xs font-bold">{label}</span>
                          </div>
                          <div className="text-xs text-slate-700 mt-1">
                            Troca a cada <strong>{p.avg_interval.toLocaleString("pt-BR")} km</strong> · Última em <strong>KM {p.last_km.toLocaleString("pt-BR")}</strong>
                          </div>
                          <div className="text-xs text-slate-700">
                            Próxima estimada: <strong>KM {p.next_estimated_km.toLocaleString("pt-BR")}</strong>
                            {p.km_since_last > 0 && <> · Rodou <strong>{p.km_since_last.toLocaleString("pt-BR")}</strong> desde a última</>}
                          </div>
                          <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                            <div className={`h-full ${status === "overdue" ? "bg-red-500" : status === "critical" ? "bg-amber-500" : status === "soon" ? "bg-yellow-500" : "bg-emerald-500"}`} style={{ width: `${Math.min(100, p.progress)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="relative pl-8">
                {/* Vertical line */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-gradient-to-b from-blue-500 via-slate-300 to-slate-200"></div>
                {vehicleItems.map((i, idx) => (
                  <div key={i.id} data-testid={`timeline-item-${i.id}`} className="relative pb-6 last:pb-0">
                    {/* Dot */}
                    <div className={`absolute -left-8 top-1 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md ${i.type === "preventiva" ? "bg-emerald-600" : "bg-amber-600"}`}>
                      {i.type === "preventiva" ? <Sparkle size={12} weight="fill" /> : <Wrench size={12} weight="fill" />}
                    </div>
                    {/* Card */}
                    <div className="border rounded-lg p-3 bg-white hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex-1 min-w-[220px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`px-2 py-0.5 rounded text-xs capitalize font-semibold ${i.type === "preventiva" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{i.type}</span>
                            <span className="text-xs text-slate-500">{fmtDate(i.date)}</span>
                            <span className="font-mono text-xs bg-slate-100 px-2 py-0.5 rounded">KM {i.km_at_service?.toLocaleString("pt-BR")}</span>
                            {i.next_service_km > 0 && <span className="font-mono text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">próx {i.next_service_km?.toLocaleString("pt-BR")}</span>}
                          </div>
                          <div className="font-semibold text-slate-900 mt-1">{i.description || "—"}</div>
                          {i.part_name && (
                            <div className="text-xs text-slate-600 mt-1 inline-flex items-center gap-1">
                              {i.part_condition === "reparada"
                                ? <><Recycle size={12} weight="fill" className="text-amber-600" /> {i.part_name} · <strong>Reparada</strong> · {i.part_quantity}× {fmt(i.part_unit_value)} = {fmt(i.part_total_value)}</>
                                : <><Sparkle size={12} weight="fill" className="text-emerald-600" /> {i.part_name} · <strong>Nova</strong> · {i.part_quantity}× {fmt(i.part_unit_value)} = {fmt(i.part_total_value)}</>
                              }
                            </div>
                          )}
                          {(i.service_place || i.city) && (
                            <div className="text-xs text-slate-500 mt-1">
                              🔧 {i.service_place}{i.city ? ` · ${i.city}${i.uf ? `/${i.uf}` : ""}` : ""}
                            </div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500">Total</div>
                          <div className="text-lg font-extrabold text-red-700">{fmt(i.cost)}</div>
                          <Button size="icon" variant="ghost" onClick={() => onRemove(i.id)} className="mt-1"><Trash size={14} className="text-red-600" /></Button>
                        </div>
                      </div>
                    </div>
                    {/* Gap between (KM diff) */}
                    {idx < vehicleItems.length - 1 && (
                      <div className="text-xs text-slate-400 mt-2 pl-1">
                        ↕ {(vehicleItems[idx].km_at_service - vehicleItems[idx + 1].km_at_service).toLocaleString("pt-BR")} km entre esta e a anterior
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Compute smart part predictions based on maintenance history.
 * For each unique part_name that appeared 1+ times, calculate:
 *  - avg_interval: km between changes (or default fallback)
 *  - last_km: km of last change
 *  - km_since_last: current_km - last_km
 *  - next_estimated_km: last_km + avg_interval
 *  - status: overdue/critical/soon/ok
 */
function computePartPredictions(vehicleItems, currentKm) {
  const byPart = {};
  for (const m of vehicleItems) {
    const name = (m.part_name || "").trim();
    if (!name) continue;
    if (!byPart[name]) byPart[name] = [];
    byPart[name].push({ km: m.km_at_service || 0, date: m.date });
  }
  const preds = [];
  // Fallback intervals by common part types (km) — used only when we have just 1 sample
  const DEFAULTS = {
    "oleo": 10000, "filtro oleo": 10000, "filtro de oleo": 10000,
    "pastilha": 25000, "pastilha freio": 25000, "pastilha de freio": 25000,
    "amortecedor": 100000, "correia": 50000, "correia dentada": 50000,
    "vela": 30000, "bateria": 60000, "pneu": 80000, "kit embreagem": 200000, "embreagem": 200000,
  };
  for (const [name, entries] of Object.entries(byPart)) {
    entries.sort((a, b) => a.km - b.km);
    const kms = entries.map((e) => e.km);
    let avgInterval = 0;
    if (kms.length >= 2) {
      const diffs = [];
      for (let i = 1; i < kms.length; i++) diffs.push(kms[i] - kms[i - 1]);
      avgInterval = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    } else {
      // Single sample — use default by matching lowercase name
      const lower = name.toLowerCase();
      for (const [k, v] of Object.entries(DEFAULTS)) {
        if (lower.includes(k)) { avgInterval = v; break; }
      }
      if (!avgInterval) continue; // skip if we can't estimate
    }
    if (avgInterval <= 0) continue;
    const lastKm = kms[kms.length - 1];
    const kmSinceLast = Math.max(0, (currentKm || lastKm) - lastKm);
    const nextEstimatedKm = lastKm + avgInterval;
    const remaining = nextEstimatedKm - (currentKm || lastKm);
    const progress = Math.max(0, Math.min(100, (kmSinceLast / avgInterval) * 100));
    let status = "ok";
    if (remaining <= 0) status = "overdue";
    else if (remaining <= avgInterval * 0.1) status = "critical"; // <=10% remaining
    else if (remaining <= avgInterval * 0.25) status = "soon";
    preds.push({
      part_name: name,
      avg_interval: Math.round(avgInterval),
      last_km: lastKm,
      km_since_last: kmSinceLast,
      next_estimated_km: Math.round(nextEstimatedKm),
      km_remaining: Math.round(remaining),
      progress,
      status,
      samples: kms.length,
    });
  }
  // Sort by urgency: overdue > critical > soon > ok
  const order = { overdue: 0, critical: 1, soon: 2, ok: 3 };
  preds.sort((a, b) => order[a.status] - order[b.status] || a.km_remaining - b.km_remaining);
  return preds;
}
